import React from 'react';
import Avatar from '../UI/Avatar';
import { Loader } from '../UI/LoadingSpinner';
import ResponsiveTableFrame from '../UI/ResponsiveTableFrame';

/**
 * Shared matrix table for statistics pages.
 *
 * Props:
 *   data            {Array}    - rows of matrix data
 *   loading         {boolean}
 *   dateColumns     {Array}    - [{key, dateStr, label, sublabel, isWeekend}]
 *   columnTotals    {Object}   - { [dateStr]: number }
 *   grandTotal      {number}
 *   totalKey        {string}   - field name on each row for the row total (e.g. 'total_talents')
 *   startIndex      {number}   - offset for row numbering
 *   onManagerClick  {Function} - (row) => void
 *   getManagerInfo  {Function} - optional (row) => {name, email, avatar}; defaults to row.manager
 *   pagination      {Object}   - optional { current_page, total_pages, total_items }
 *   onPageChange    {Function} - optional (page) => void
 *   itemsPerPage    {number}   - optional, for "Showing X–Y of Z"
 */
const defaultGetManagerInfo = (row) => ({
  avatar: row.manager?.avatar,
  name:   row.manager?.name,
  email:  row.manager?.email,
});

const MatrixTable = ({
  data = [],
  loading = false,
  dateColumns = [],
  columnTotals = {},
  grandTotal = 0,
  totalKey,
  startIndex = 0,
  onManagerClick,
  getManagerInfo = defaultGetManagerInfo,
  pagination,
  onPageChange,
  itemsPerPage,
}) => {
  return (
    <div className="min-w-0 overflow-hidden rounded-lg border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
      {loading ? (
        <div className="flex justify-center items-center min-h-[200px]">
          <Loader />
        </div>
      ) : data.length === 0 ? (
        <div className="flex justify-center items-center min-h-[200px] text-gray-400 dark:text-gray-500">
          No data for selected period
        </div>
      ) : (
        <ResponsiveTableFrame tableClassName="max-w-full" scrollLabel="Scrollable manager statistics matrix">
          <table className="min-w-max text-sm border-separate border-spacing-0">
            <thead>
              <tr className="bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
                {/* # */}
                <th className="sticky left-0 z-20 bg-gray-50 dark:bg-gray-700 px-3 py-3 text-center font-semibold text-gray-600 dark:text-gray-300 border-r border-gray-200 dark:border-gray-600 min-w-[40px]">
                  #
                </th>
                {/* Manager */}
                <th className="sticky left-[40px] z-20 w-[150px] min-w-[150px] border-r border-gray-200 bg-gray-50 px-3 py-3 text-left font-semibold text-gray-600 shadow-[4px_0_6px_-5px_rgba(0,0,0,0.45)] dark:border-gray-600 dark:bg-gray-700 dark:text-gray-300 sm:w-[200px] sm:min-w-[200px] sm:px-4">
                  Manager
                </th>
                {/* Date columns */}
                {dateColumns.map(col => (
                  <th
                    key={col.key}
                    className={`px-2 py-3 text-center border-r border-gray-200 dark:border-gray-600 min-w-[40px] ${
                      col.isWeekend ? 'bg-gray-100 dark:bg-gray-600' : ''
                    }`}
                  >
                    <div className="flex flex-col items-center gap-0.5">
                      <span className="font-semibold text-gray-600 dark:text-gray-300 text-xs">{col.label}</span>
                      {col.sublabel && (
                        <span className="text-[10px] text-gray-400 leading-none">{col.sublabel}</span>
                      )}
                    </div>
                  </th>
                ))}
                {/* TOTAL */}
                <th className="px-3 py-3 text-center font-bold text-white bg-teal-600 min-w-[60px] whitespace-nowrap">
                  TOTAL
                </th>
              </tr>
            </thead>

            <tbody className="[&>tr>td]:border-b [&>tr>td]:border-gray-100 dark:[&>tr>td]:border-gray-700">
              {data.map((row, index) => {
                const mgr = getManagerInfo(row);
                return (
                  <tr
                    key={row.manager_id}
                    className="group hover:bg-gray-50 dark:hover:bg-gray-700/60 transition-colors"
                  >
                    {/* # */}
                    <td className="sticky left-0 z-10 bg-white dark:bg-gray-800 group-hover:bg-gray-50 dark:group-hover:bg-[#2d3747] px-3 py-3 text-center text-gray-400 dark:text-gray-500 border-r border-gray-200 dark:border-gray-600 text-xs font-medium transition-colors min-w-[40px]">
                      {startIndex + index + 1}
                    </td>
                    {/* Manager */}
                    <td className="sticky left-[40px] z-10 w-[150px] min-w-[150px] max-w-[150px] overflow-hidden border-r border-gray-200 bg-white px-3 py-3 shadow-[4px_0_6px_-5px_rgba(0,0,0,0.45)] transition-colors group-hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:group-hover:bg-[#2d3747] sm:w-[200px] sm:min-w-[200px] sm:max-w-[200px] sm:px-4">
                      <div
                        className="flex items-center gap-2 cursor-pointer min-w-0"
                        onClick={() => onManagerClick?.(row)}
                      >
                        <Avatar
                          avatar={mgr.avatar}
                          name={mgr.name || row.manager_id}
                          size="sm"
                          className="flex-shrink-0"
                        />
                        <div className="flex flex-col min-w-0">
                          <span className="font-medium text-gray-900 dark:text-gray-100 hover:text-primary-600 dark:hover:text-primary-400 transition-colors truncate">
                            {mgr.name || `Manager ${row.manager_id}`}
                          </span>
                          {mgr.email && (
                            <span className="text-xs text-gray-400 truncate">{mgr.email}</span>
                          )}
                        </div>
                      </div>
                    </td>
                    {/* Day cells */}
                    {dateColumns.map(col => {
                      const count = row.days?.[col.dateStr] || 0;
                      return (
                        <td
                          key={col.key}
                          className={`px-2 py-3 text-center border-r border-gray-100 dark:border-gray-700 ${
                            col.isWeekend ? 'bg-gray-50 dark:bg-gray-700/30' : ''
                          } ${
                            count > 0
                              ? 'font-semibold text-gray-900 dark:text-gray-100'
                              : 'text-gray-300 dark:text-gray-600'
                          }`}
                        >
                          {count > 0 ? count : '—'}
                        </td>
                      );
                    })}
                    {/* Row total */}
                    <td className="px-3 py-3 text-center font-bold text-white bg-teal-600">
                      {row[totalKey] ?? 0}
                    </td>
                  </tr>
                );
              })}
            </tbody>

            <tfoot>
              <tr className="border-t-2 border-teal-500 bg-teal-50 dark:bg-teal-900/20">
                <td className="sticky left-0 z-10 bg-teal-50 dark:bg-[#1b343d] px-3 py-3 border-r border-gray-200 dark:border-gray-600 min-w-[40px]" />
                <td className="sticky left-[40px] z-10 w-[150px] min-w-[150px] whitespace-nowrap border-r border-gray-200 bg-teal-50 px-3 py-3 font-bold text-teal-700 shadow-[4px_0_6px_-5px_rgba(0,0,0,0.45)] dark:border-gray-600 dark:bg-[#1b343d] dark:text-teal-300 sm:w-[200px] sm:min-w-[200px] sm:px-4">
                  Total
                </td>
                {dateColumns.map(col => {
                  const colTotal = columnTotals[col.dateStr] || 0;
                  return (
                    <td
                      key={col.key}
                      className={`px-2 py-3 text-center border-r border-gray-100 dark:border-gray-700 font-semibold ${
                        colTotal > 0 ? 'text-teal-700 dark:text-teal-300' : 'text-gray-300 dark:text-gray-600'
                      } ${col.isWeekend ? 'bg-teal-100/50 dark:bg-teal-900/40' : ''}`}
                    >
                      {colTotal > 0 ? colTotal : '—'}
                    </td>
                  );
                })}
                <td className="px-3 py-3 text-center font-bold text-white bg-teal-700">
                  {grandTotal}
                </td>
              </tr>
            </tfoot>
          </table>
        </ResponsiveTableFrame>
      )}

      {/* Pagination */}
      {!loading && data.length > 0 && pagination && pagination.total_pages > 1 && (
        <div className="flex flex-col gap-3 border-t border-gray-200 px-4 py-3 text-sm text-gray-600 dark:border-gray-700 dark:text-gray-400 sm:flex-row sm:items-center sm:justify-between">
          <span>
            Showing {startIndex + 1}–{Math.min(startIndex + (itemsPerPage ?? data.length), pagination.total_items)} of {pagination.total_items}
          </span>
          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={() => onPageChange?.(pagination.current_page - 1)}
              disabled={pagination.current_page <= 1}
              className="px-3 py-1 rounded border border-gray-300 dark:border-gray-600 disabled:opacity-40 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              ←
            </button>
            <span>{pagination.current_page} / {pagination.total_pages}</span>
            <button
              onClick={() => onPageChange?.(pagination.current_page + 1)}
              disabled={pagination.current_page >= pagination.total_pages}
              className="px-3 py-1 rounded border border-gray-300 dark:border-gray-600 disabled:opacity-40 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              →
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MatrixTable;
